<template>
  <div class="page-container">
    <div class="page-content">
      <!-- 페이지 헤더 -->
      <div class="page-header mb-6">
        <div class="flex justify-between items-start">
          <div>
            <h1 class="page-title">스케줄러 설정</h1>
            <p class="page-subtitle">
              시스템 스케줄러의 실행 상태를 관리합니다
            </p>
          </div>
        </div>
      </div>

      <!-- 스케줄러 설정 목록 -->
      <div class="card-base">
        <!-- 로딩 상태 -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>

        <!-- 에러 상태 -->
        <div v-else-if="error" class="text-center py-12">
          <div class="text-red-500 mb-4">
            <i class="fas fa-exclamation-triangle text-2xl"></i>
          </div>
          <p class="text-gray-600 mb-4">스케줄러 설정을 불러오는데 실패했습니다.</p>
          <button @click="fetchSchedulerSettings" class="btn-primary">
            <i class="fas fa-redo mr-2"></i>다시 시도
          </button>
        </div>

        <!-- 스케줄러 목록 -->
        <div v-else-if="schedulerSettings.length > 0" class="space-y-4">
          <div
            v-for="scheduler in schedulerSettings"
            :key="scheduler.id"
            class="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <!-- 스케줄러 정보 -->
            <div class="flex-1">
              <div class="flex items-center space-x-3 mb-2">
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ scheduler.typeTitle }}
                </h3>
                <span
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="scheduler.active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'"
                >
                  {{ scheduler.active ? '실행중' : '중지됨' }}
                </span>
              </div>
              
              <div class="space-y-1">
                <p class="text-sm text-gray-600">
                  <span class="font-medium">실행 주기:</span>
                  <code class="ml-2 px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                    {{ scheduler.cronExpression }}
                  </code>
                </p>
                <p v-if="scheduler.description" class="text-sm text-gray-600">
                  <span class="font-medium">설명:</span>
                  {{ scheduler.description }}
                </p>
              </div>
            </div>

            <!-- 토글 스위치 -->
            <div class="flex items-center space-x-3">
              <button
                @click="toggleScheduler(scheduler)"
                :disabled="toggleLoading[scheduler.type]"
                class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="scheduler.active ? 'bg-primary-600' : 'bg-gray-200'"
                role="switch"
                :aria-checked="scheduler.active"
              >
                <span
                  class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  :class="scheduler.active ? 'translate-x-5' : 'translate-x-0'"
                ></span>
              </button>
              
              <!-- 로딩 스피너 -->
              <div
                v-if="toggleLoading[scheduler.type]"
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"
              ></div>
            </div>
          </div>
        </div>

        <!-- 빈 상태 -->
        <div v-else class="text-center py-12">
          <div class="text-gray-400 mb-4">
            <i class="fas fa-clock text-3xl"></i>
          </div>
          <p class="text-gray-600">등록된 스케줄러가 없습니다.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue';
import { schedulerService } from '@/service';

export default {
  name: 'SchedulerSettingView',
  setup() {
    const loading = ref(false);
    const error = ref(false);
    const schedulerSettings = ref([]);
    const toggleLoading = reactive({});

    /**
     * 성공 메시지 표시
     */
    const showSuccess = (message) => {
      // 커스텀 토스트 이벤트 발생
      const event = new CustomEvent('show-toast', {
        detail: { message, type: 'success' },
      });
      window.dispatchEvent(event);
    };

    /**
     * 에러 메시지 표시
     */
    const showError = (message) => {
      // 커스텀 토스트 이벤트 발생 (fallback으로 alert 사용)
      const event = new CustomEvent('show-toast', {
        detail: { message, type: 'error' },
      });
      window.dispatchEvent(event);
      
      // 토스트가 처리되지 않을 경우를 대비한 fallback
      setTimeout(() => {
        alert(message);
      }, 100);
    };

    /**
     * 스케줄러 설정 목록 조회
     */
    const fetchSchedulerSettings = async () => {
      loading.value = true;
      error.value = false;
      
      try {
        const data = await schedulerService.getSchedulerSettings();
        schedulerSettings.value = data;
      } catch (err) {
        console.error('스케줄러 설정 조회 실패:', err);
        error.value = true;
        showError('스케줄러 설정을 불러오는데 실패했습니다.');
      } finally {
        loading.value = false;
      }
    };

    /**
     * 스케줄러 토글
     */
    const toggleScheduler = async (scheduler) => {
      const newStatus = !scheduler.active;
      toggleLoading[scheduler.type] = true;
      
      try {
        await schedulerService.toggleScheduler(scheduler.type, newStatus);
        
        // 로컬 상태 업데이트
        scheduler.active = newStatus;
        
        showSuccess(
          `${scheduler.typeTitle} 스케줄러가 ${newStatus ? '활성화' : '비활성화'}되었습니다.`
        );
      } catch (err) {
        console.error('스케줄러 토글 실패:', err);
        showError('스케줄러 상태 변경에 실패했습니다.');
      } finally {
        toggleLoading[scheduler.type] = false;
      }
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      fetchSchedulerSettings();
    });

    return {
      loading,
      error,
      schedulerSettings,
      toggleLoading,
      fetchSchedulerSettings,
      toggleScheduler,
    };
  },
};
</script>

<style scoped>
/* 토글 스위치 애니메이션 */
.toggle-switch {
  transition: all 0.2s ease-in-out;
}

/* 코드 블록 스타일 */
code {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
}
</style>
